/* How many guests to show at a time, remembered — STORY-017.
 *
 * The story asks for the choice to persist "as the Member moves between
 * Events", which rules out the URL: a guest list lives inside one Event's
 * screen, so a query parameter there would be forgotten the moment somebody
 * opened a different night.
 *
 * So it is a stored preference rather than view state. It is not personal data
 * and not a credential — `security.md` forbids a session token in
 * `localStorage` and says nothing about a number somebody chose from a select,
 * which is exactly the kind of thing it is for.
 */

import { GUESTS_PAGE_MAX, GUESTS_PAGE_SIZE, GUESTS_PAGE_SIZES } from '@eventuley/contracts';

const STORAGE_KEY = 'eventuley.guests.page-size';

/** "All" is the server's ceiling, not an unbounded read — see the contract. */
export const ALL_GUESTS = GUESTS_PAGE_MAX;

export type GuestPageSize = (typeof GUESTS_PAGE_SIZES)[number] | typeof ALL_GUESTS;

const isPageSize = (value: number): value is GuestPageSize =>
  value === ALL_GUESTS || (GUESTS_PAGE_SIZES as readonly number[]).includes(value);

/**
 * The remembered page size, or the default.
 *
 * Every failure answers the default rather than throwing: storage can be
 * disabled outright in a private window, and a value can be anything at all
 * once somebody has opened developer tools or a previous version has written a
 * size this one no longer offers. A guest list that will not render because a
 * preference is stale is a worse outcome than one showing ten rows.
 */
export function readPageSize(): GuestPageSize {
  try {
    const stored = Number(globalThis.localStorage?.getItem(STORAGE_KEY));
    return isPageSize(stored) ? stored : GUESTS_PAGE_SIZE;
  } catch {
    return GUESTS_PAGE_SIZE;
  }
}

export function writePageSize(size: GuestPageSize): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, String(size));
  } catch {
    /* Storage is full, or disabled. The choice still applies for this screen —
     * it simply will not be there next time, which is a smaller failure than
     * refusing to change the page size at all. */
  }
}
